import React, { useState, useEffect, useCallback, useRef } from "react";

import { useNavigate, useParams, useLocation } from "react-router-dom";

import FollowButton from "../components/FollowButton";
import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import apiService from "../services/api";
import {
  isPlayerFollowed,
  setPlayerNotificationPref,
} from "../utils/sportsPreferences";
import webPlayerStorage from "../utils/webPlayerStorage";

// ─── SPORT STAT FIELDS ───
const SPORT_STAT_FIELDS = {
  Soccer: [
    { key: "goals", label: "Goals" },
    { key: "assists", label: "Assists" },
    { key: "appearances", label: "Apps" },
    { key: "yellowCards", label: "Yellow" },
    { key: "redCards", label: "Red" },
  ],
  Basketball: [
    { key: "ppg", label: "PPG" },
    { key: "rpg", label: "RPG" },
    { key: "apg", label: "APG" },
    { key: "fgPct", label: "FG%" },
    { key: "spg", label: "SPG" },
  ],
  "Ice Hockey": [
    { key: "goals", label: "Goals" },
    { key: "assists", label: "Assists" },
    { key: "points", label: "Points" },
    { key: "plusMinus", label: "+/-" },
    { key: "pim", label: "PIM" },
  ],
  "American Football": [
    { key: "passingYards", label: "Pass Yds" },
    { key: "rushingYards", label: "Rush Yds" },
    { key: "touchdowns", label: "TDs" },
    { key: "tackles", label: "Tackles" },
    { key: "interceptions", label: "INT" },
  ],
};

// ─── SECTION HEADER COMPONENT ───
const SectionHeader = ({ title, icon }) => (
  <div className="flex items-center gap-2 mb-5">
    {icon && <i className={`fas ${icon} text-white/20 text-sm`} />}
    <h2 className="text-xl font-bold text-white">{title}</h2>
  </div>
);

const mapV2PlayerToSportsDb = (dto) => {
  if (!dto) return null;
  const fanart = dto.images?.fanart || [];

  return {
    idPlayer: dto.id,
    strPlayer: dto.name,
    strLastName: dto.lastName,
    strNationality: dto.nationality,
    strTeam: dto.team,
    idTeam: dto.teamId,
    strSport: dto.sport,
    strPosition: dto.position,
    strNumber: dto.number,
    strHeight: dto.height,
    strWeight: dto.weight,
    strWage: dto.wage,
    strStatus: dto.status,
    strGender: dto.gender,
    strDescriptionEN: dto.description,
    dateBorn: dto.birthDate,
    strBirthLocation: dto.birthLocation,
    strThumb: dto.images?.thumb,
    strCutout: dto.images?.cutout,
    strRender: dto.images?.render,
    strPoster: dto.images?.poster,
    strBanner: dto.images?.banner,
    strFanart1: fanart[0],
    strFanart2: fanart[1],
    strFanart3: fanart[2],
    strFanart4: fanart[3],
    strFacebook: dto.social?.facebook,
    strTwitter: dto.social?.twitter,
    strInstagram: dto.social?.instagram,
    strYoutube: dto.social?.youtube,
    strWebsite: dto.social?.website,
  };
};

const fixtureToRecentGame = (fixture, playerTeam) => {
  if (!fixture) return null;
  const { home } = fixture;
  const { away } = fixture;
  const isHome = playerTeam && home?.name === playerTeam;
  const opponent = isHome ? away?.name : home?.name;
  const homeScore = fixture.scores?.home ?? fixture.scores?.fullTime?.home;
  const awayScore = fixture.scores?.away ?? fixture.scores?.fullTime?.away;
  let result = "-";

  if (fixture.status?.isFinished && homeScore != null && awayScore != null) {
    const myScore = isHome ? homeScore : awayScore;
    const oppScore = isHome ? awayScore : homeScore;
    const prefix = myScore > oppScore ? "W" : myScore < oppScore ? "L" : "D";

    result = `${prefix} ${homeScore}-${awayScore}`;
  }

  return {
    date: fixture.date ? fixture.date.slice(0, 10) : "",
    opponent: opponent || "—",
    result,
    rating: "-",
    keyStats: fixture.league?.name || "",
  };
};

const careerToHistory = (entry) => ({
  club: entry?.team || "—",
  years:
    entry?.joined && entry?.departed
      ? `${entry.joined}-${entry.departed}`
      : entry?.joined || "",
  apps: null,
  goals: null,
  logo: entry?.badge || null,
});

const honourToTrophy = (h) => ({
  name: h?.name || "",
  count: 1,
  year: h?.season || "",
  icon: "fa-trophy",
});

const milestoneToCard = (m) => ({
  title: m?.name || "",
  date: m?.date || "",
  description: m?.team || "",
  icon: "fa-flag-checkered",
});

// ─── BIO INFO CARD ───
const InfoCard = ({ label, value }) => (
  <div className="bg-white/5 rounded-xl p-4 border border-white/[0.08]">
    <p className="text-[10px] font-bold text-white/30 uppercase tracking-wider mb-1">
      {label}
    </p>
    <p className="text-white font-semibold text-sm">{value}</p>
  </div>
);

// ─── MAIN COMPONENT ───
const WebPlayerPlayerDetail = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const hasCheckedProfile = useRef(false);

  const [player, setPlayer] = useState(null);
  const [career, setCareer] = useState([]);
  const [honours, setHonours] = useState([]);
  const [milestonesData, setMilestonesData] = useState([]);
  const [recentGamesData, setRecentGamesData] = useState([]);
  const [teamBadge, setTeamBadge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followVersion, setFollowVersion] = useState(0);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const profile = webPlayerStorage.getProfile();
  const playlist = webPlayerStorage.getPlaylist();

  const followStatus = isPlayerFollowed(
    playerId,
    player?.strPlayer || player?.name || ""
  );

  const playerKey = followStatus.key;
  const notifPrefs = followStatus.followed
    ? (() => {
        const prefs = JSON.parse(
          localStorage.getItem("sportsPreferences") || "{}"
        );

        return prefs.playerFollows?.[playerKey]?.notifications || {};
      })()
    : {};

  const buildStatePlayer = useCallback(() => {
    const sp = location.state?.player;

    if (!sp) return null;

    return {
      idPlayer: sp.id || playerId,
      strPlayer: sp.name,
      strPosition: sp.position || null,
      strCutout: sp.thumbUrl || sp.photo || null,
      strThumb: sp.thumbUrl || sp.photo || null,
      strNationality: sp.nationality || null,
      strHeight: sp.height || null,
      strDescriptionEN: sp.bio || null,
      strNumber: sp.number || null,
      dateBorn: sp.age ? `${new Date().getFullYear() - sp.age}-01-01` : null,
      strTeam: sp.teamName || null,
      strSport: sp.sport || null,
      _fromState: true,
    };
  }, [location.state, playerId]);

  const fetchPlayerData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getPlayerProfile(playerId);
      const dto = response?.data;

      if (dto) {
        const p = mapV2PlayerToSportsDb(dto);

        setPlayer(p);
        setCareer(dto.career || []);
        setHonours(dto.honours || []);
        setMilestonesData(dto.milestones || []);
        setRecentGamesData(dto.recentGames || []);

        const careerCurrent = (dto.career || []).find(
          (c) => c?.team === dto.team
        );

        if (careerCurrent?.badge) setTeamBadge(careerCurrent.badge);

        return true;
      }

      return false;
    } catch (err) {
      console.error("Failed to fetch player:", err);

      return false;
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  useEffect(() => {
    if (!hasCheckedProfile.current) {
      hasCheckedProfile.current = true;
      const p = webPlayerStorage.getProfile();

      if (!p || !p.id) {
        navigate("/web-player/profiles");

        return;
      }
    }

    const load = async () => {
      const fetched = await fetchPlayerData();

      if (!fetched) {
        const statePlayer = buildStatePlayer();

        if (statePlayer) {
          setPlayer(statePlayer);
        }
        setLoading(false);
      }
    };

    load();
  }, [fetchPlayerData, navigate, buildStatePlayer]);

  const handleBack = () => navigate(-1);

  const handleToggleNotif = (key) => {
    const current = notifPrefs[key] ?? true;

    setPlayerNotificationPref(playerKey, key, !current);
    setFollowVersion((v) => v + 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-500 mb-4" />
          <p className="text-white/40 text-sm">Loading player...</p>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-user-slash text-4xl text-white/20 mb-4" />
          <p className="text-white/40">Player not found</p>
          <button
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
            onClick={handleBack}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const age = player.dateBorn
    ? Math.floor(
        (Date.now() - new Date(player.dateBorn).getTime()) /
          (365.25 * 24 * 60 * 60 * 1000)
      )
    : null;
  const sport = player.strSport || "Soccer";
  const sportFields = SPORT_STAT_FIELDS[sport] || [];
  const effectiveStats = null;
  const recentGames = recentGamesData
    .map((f) => fixtureToRecentGame(f, player.strTeam))
    .filter(Boolean);
  const careerHistory = career.map(careerToHistory);
  const trophies = honours.map(honourToTrophy);
  const milestones = milestonesData.map(milestoneToCard);
  const awards = [];
  const intlStats = null;

  // Build gallery images from available fields
  const galleryImages = [
    player.strPoster && { src: player.strPoster, label: "Poster" },
    player.strBanner && { src: player.strBanner, label: "Banner" },
    player.strRender && { src: player.strRender, label: "Render" },
    player.strCartoon && { src: player.strCartoon, label: "Cartoon" },
    player.strFanart1 && { src: player.strFanart1, label: "Fanart 1" },
    player.strFanart2 && { src: player.strFanart2, label: "Fanart 2" },
    player.strFanart3 && { src: player.strFanart3, label: "Fanart 3" },
    player.strFanart4 && { src: player.strFanart4, label: "Fanart 4" },
    player.strCutout && { src: player.strCutout, label: "Cutout" },
    player.strThumb && { src: player.strThumb, label: "Thumbnail" },
  ].filter(Boolean);

  // Social links
  const socialLinks = [
    player.strFacebook && {
      url: `https://${player.strFacebook}`,
      icon: "fa-brands fa-facebook",
      label: "Facebook",
      color: "hover:text-blue-400",
    },
    player.strInstagram && {
      url: `https://${player.strInstagram}`,
      icon: "fa-brands fa-instagram",
      label: "Instagram",
      color: "hover:text-pink-400",
    },
    player.strTwitter && {
      url: `https://${player.strTwitter}`,
      icon: "fa-brands fa-x-twitter",
      label: "X / Twitter",
      color: "hover:text-white",
    },
    player.strYoutube && {
      url: `https://${player.strYoutube}`,
      icon: "fa-brands fa-youtube",
      label: "YouTube",
      color: "hover:text-red-400",
    },
    player.strWebsite && {
      url: player.strWebsite.startsWith("http")
        ? player.strWebsite
        : `https://${player.strWebsite}`,
      icon: "fas fa-globe",
      label: "Website",
      color: "hover:text-green-400",
    },
  ].filter(Boolean);

  const externalLinks = [
    player.idWikidata && {
      url: `https://www.wikidata.org/wiki/${player.idWikidata}`,
      icon: "fa-brands fa-wikipedia-w",
      label: "Wikipedia",
    },
    player.idTransferMkt && {
      url: `https://www.transfermarkt.com/player/profil/spieler/${player.idTransferMkt}`,
      icon: "fas fa-exchange-alt",
      label: "Transfermarkt",
    },
    player.idESPN && {
      url: `https://www.espn.com/soccer/player/_/id/${player.idESPN}`,
      icon: "fas fa-tv",
      label: "ESPN",
    },
  ].filter(Boolean);

  // Preferred foot/side label
  const sideLabel =
    sport === "Soccer" || sport === "Ice Hockey"
      ? "Preferred Foot"
      : "Dominant Hand";

  return (
    <div className="min-h-screen text-white">
      <LeftMenu
        isSportsPage
        activeProfile={profile}
        playlist={playlist}
        selectedSection="sports"
      />
      <MobileMenu activeProfile={profile} playlist={playlist} />

      {/* ─── 1. ENHANCED HERO (edge-to-edge) ─── */}
      <div className="relative h-[50vh] min-h-[420px] overflow-hidden">
        <img
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          src={
            player.strBanner ||
            player.strFanart1 ||
            player.strFanart2 ||
            player.strThumb ||
            "/background.png"
          }
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        {/* Back button */}
        <button
          className="absolute top-6 left-6 md:left-[88px] z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors backdrop-blur-sm"
          onClick={handleBack}
        >
          <i className="fas fa-arrow-left text-white" />
        </button>

        {/* Player info overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:pl-[104px] md:pr-12 md:pb-12 flex items-end gap-8">
          {(player.strRender || player.strCutout || player.strThumb) && (
            <img
              alt={player.strPlayer}
              className="w-40 h-40 md:w-52 md:h-52 object-contain drop-shadow-2xl"
              src={player.strRender || player.strCutout || player.strThumb}
            />
          )}
          <div className="flex-1 min-w-0">
            {/* Status + Number + Position */}
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              {player.strStatus && (
                <span
                  className={`px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${
                    player.strStatus === "Retired"
                      ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                      : "bg-green-500/20 text-green-400 border border-green-500/30"
                  }`}
                >
                  {player.strStatus}
                </span>
              )}
              {player.strNumber && (
                <span className="text-blue-400 font-bold text-lg">
                  #{player.strNumber}
                </span>
              )}
              {player.strPosition && (
                <span className="text-white/40 text-sm">
                  {player.strPosition}
                </span>
              )}
              {player.strSide && (
                <span className="px-2 py-0.5 text-[10px] font-semibold bg-white/10 text-white/50 rounded-full border border-white/[0.08]">
                  {player.strSide}{" "}
                  {sideLabel === "Preferred Foot" ? "foot" : "hand"}
                </span>
              )}
            </div>

            {/* Name */}
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-1 truncate">
              {player.strPlayer}
            </h1>

            {/* Alternate name */}
            {player.strPlayerAlternate &&
              player.strPlayerAlternate !== player.strPlayer && (
                <p className="text-white/30 text-sm mb-2 italic">
                  {player.strPlayerAlternate}
                </p>
              )}

            {/* Team + National Team */}
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              {player.strTeam && (
                <button
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                  onClick={() =>
                    navigate(
                      `/web-player/sports/team/${encodeURIComponent(player.strTeam)}`
                    )
                  }
                >
                  <i className="fas fa-shield-halved mr-1.5 text-xs" />
                  {player.strTeam}
                </button>
              )}
              {player.strTeam2 && (
                <span className="text-white/40 text-sm">
                  <i className="fas fa-flag mr-1.5 text-xs" />
                  {player.strTeam2}
                </span>
              )}
            </div>

            {/* Follow button */}
            <FollowButton
              entity={{
                id: player.idPlayer,
                name: player.strPlayer,
                photo: player.strCutout || player.strThumb,
                position: player.strPosition,
                teamName: player.strTeam,
                sport: player.strSport,
              }}
              key={followVersion}
              size="lg"
              type="player"
              variant="full"
              onFollowChange={() => setFollowVersion((v) => v + 1)}
            />
          </div>
        </div>
      </div>

      {/* Gradient transition from hero into app_background */}
      <div
        className="h-32"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)",
        }}
      />

      {/* ─── CONTENT ─── */}
      <div className="ml-0 md:ml-[72px] -mt-24 relative z-10">
        {/* ─── TAB BAR ─── */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-6">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide pb-1">
            {[
              { key: "overview", label: "Overview", icon: "fa-th-large" },
              {
                key: "stats",
                label: "Stats & Games",
                icon: "fa-chart-bar",
              },
              { key: "career", label: "Career", icon: "fa-briefcase" },
              { key: "honours", label: "Honours", icon: "fa-trophy" },
              { key: "media", label: "Media", icon: "fa-images" },
            ].map((tab) => (
              <button
                className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.key
                    ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                    : "bg-white/5 text-white/40 border border-white/[0.06] hover:bg-white/10 hover:text-white/60"
                }`}
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                <i className={`fas ${tab.icon} text-xs`} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-8 space-y-12">
          {/* ═══════════════ OVERVIEW TAB ═══════════════ */}
          {activeTab === "overview" && (
            <>
              {/* Personal Info + Bio */}
              <section>
                <SectionHeader icon="fa-user" title="Personal Info" />
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
                  {player.strNationality && (
                    <InfoCard
                      label="Nationality"
                      value={player.strNationality}
                    />
                  )}
                  {age && <InfoCard label="Age" value={age} />}
                  {player.dateBorn && (
                    <InfoCard
                      label="Date of Birth"
                      value={new Date(player.dateBorn).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )}
                    />
                  )}
                  {player.strBirthLocation && (
                    <InfoCard
                      label="Birth Location"
                      value={player.strBirthLocation}
                    />
                  )}
                  {player.strHeight && (
                    <InfoCard label="Height" value={player.strHeight} />
                  )}
                  {player.strWeight && (
                    <InfoCard label="Weight" value={player.strWeight} />
                  )}
                  {player.strSide && (
                    <InfoCard label={sideLabel} value={player.strSide} />
                  )}
                  {player.strGender && (
                    <InfoCard label="Gender" value={player.strGender} />
                  )}
                  {player.strCollege && (
                    <InfoCard label="College" value={player.strCollege} />
                  )}
                  {player.strPlayerAlternate &&
                    player.strPlayerAlternate !== player.strPlayer && (
                      <InfoCard
                        label="Birth Name"
                        value={player.strPlayerAlternate}
                      />
                    )}
                </div>
                {player.strDescriptionEN && (
                  <div className="relative">
                    <p
                      className={`text-white/60 text-sm leading-relaxed ${!bioExpanded ? "line-clamp-4" : ""}`}
                    >
                      {player.strDescriptionEN}
                    </p>
                    {player.strDescriptionEN.length > 300 && (
                      <button
                        className="mt-2 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                        onClick={() => setBioExpanded(!bioExpanded)}
                      >
                        {bioExpanded ? (
                          <>
                            Show less <i className="fas fa-chevron-up ml-1" />
                          </>
                        ) : (
                          <>
                            Read more <i className="fas fa-chevron-down ml-1" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </section>

              {/* Team */}
              {player.strTeam && (
                <section>
                  <SectionHeader icon="fa-shield-halved" title="Team" />
                  <button
                    className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/[0.08] hover:border-blue-500/40 transition-all w-full text-left group"
                    onClick={() =>
                      navigate(
                        `/web-player/sports/team/${encodeURIComponent(player.strTeam)}`
                      )
                    }
                  >
                    {teamBadge ? (
                      <img
                        alt=""
                        className="w-12 h-12 object-contain"
                        src={teamBadge}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-blue-600/20 flex items-center justify-center">
                        <span className="text-lg font-bold text-blue-400">
                          {player.strTeam?.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-white font-semibold group-hover:text-blue-300 transition-colors">
                        {player.strTeam}
                      </p>
                      <p className="text-white/40 text-xs">{player.strSport}</p>
                    </div>
                    <i className="fas fa-chevron-right text-white/20 group-hover:text-white/40 transition-colors" />
                  </button>
                </section>
              )}

              {/* National Team */}
              {player.strTeam2 && (
                <section>
                  <SectionHeader icon="fa-flag" title="International" />
                  <div className="bg-white/5 rounded-xl p-6 border border-white/[0.08]">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-blue-600/20 flex items-center justify-center">
                        <i className="fas fa-flag text-blue-400 text-xl" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-lg">
                          {player.strTeam2}
                        </p>
                        <p className="text-white/40 text-sm">
                          National / International Team
                        </p>
                      </div>
                    </div>
                    {intlStats && sport !== "American Football" && (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-xs font-bold text-white/30 uppercase tracking-wider">
                            International Stats
                          </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {intlStats.caps && (
                            <div className="text-center p-3 bg-white/[0.03] rounded-lg">
                              <p className="text-2xl font-bold text-white">
                                {intlStats.caps}
                              </p>
                              <p className="text-[10px] font-bold text-white/30 uppercase tracking-wider">
                                Caps
                              </p>
                            </div>
                          )}
                          {intlStats.goals && (
                            <div className="text-center p-3 bg-white/[0.03] rounded-lg">
                              <p className="text-2xl font-bold text-white">
                                {intlStats.goals}
                              </p>
                              <p className="text-[10px] font-bold text-white/30 uppercase tracking-wider">
                                Goals
                              </p>
                            </div>
                          )}
                          {intlStats.tournaments && (
                            <div className="text-center p-3 bg-white/[0.03] rounded-lg">
                              <p className="text-2xl font-bold text-white">
                                {intlStats.tournaments}
                              </p>
                              <p className="text-[10px] font-bold text-white/30 uppercase tracking-wider">
                                Tournaments
                              </p>
                            </div>
                          )}
                          {intlStats.debut && (
                            <div className="text-center p-3 bg-white/[0.03] rounded-lg">
                              <p className="text-lg font-bold text-white">
                                {new Date(intlStats.debut).getFullYear()}
                              </p>
                              <p className="text-[10px] font-bold text-white/30 uppercase tracking-wider">
                                Debut
                              </p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </section>
              )}

              {/* Social & External Links */}
              {(socialLinks.length > 0 || externalLinks.length > 0) && (
                <section>
                  <SectionHeader icon="fa-link" title="Links & Profiles" />
                  <div className="flex flex-wrap gap-3">
                    {socialLinks.map((link, i) => (
                      <a
                        className={`inline-flex items-center gap-2.5 px-4 py-3 bg-white/5 rounded-xl border border-white/[0.08] hover:border-white/20 transition-all text-white/60 ${link.color}`}
                        href={link.url}
                        key={i}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <i className={`${link.icon} text-lg`} />
                        <span className="text-sm font-medium">
                          {link.label}
                        </span>
                        <i className="fas fa-external-link-alt text-[10px] text-white/20" />
                      </a>
                    ))}
                    {externalLinks.map((link, i) => (
                      <a
                        className="inline-flex items-center gap-2.5 px-4 py-3 bg-white/5 rounded-xl border border-white/[0.08] hover:border-white/20 transition-all text-white/60 hover:text-white"
                        href={link.url}
                        key={i}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <i className={`${link.icon} text-lg`} />
                        <span className="text-sm font-medium">
                          {link.label}
                        </span>
                        <i className="fas fa-external-link-alt text-[10px] text-white/20" />
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {/* Notification Prefs */}
              {followStatus.followed && (
                <section>
                  <SectionHeader
                    icon="fa-bell"
                    title="Notification Preferences"
                  />
                  <div className="flex flex-wrap gap-3">
                    {[
                      {
                        key: "goalOrAssist",
                        label: "Goal / Assist",
                        icon: "fa-futbol",
                      },
                      {
                        key: "startingLineup",
                        label: "Starting Lineup",
                        icon: "fa-list-ol",
                      },
                    ].map((item) => {
                      const isOn = notifPrefs[item.key] !== false;

                      return (
                        <button
                          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                            isOn
                              ? "bg-blue-500/15 border border-blue-500/30 text-blue-300"
                              : "bg-white/5 border border-white/[0.08] text-white/30"
                          }`}
                          key={item.key}
                          onClick={() => handleToggleNotif(item.key)}
                        >
                          <i className={`fas ${item.icon} text-xs`} />
                          {item.label}
                          <i
                            className={`fas ${isOn ? "fa-toggle-on text-blue-400" : "fa-toggle-off text-white/20"} ml-1`}
                          />
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}

          {/* ═══════════════ STATS & GAMES TAB ═══════════════ */}
          {activeTab === "stats" && (
            <>
              {/* Season Stats */}
              {effectiveStats && sportFields.length > 0 && (
                <section>
                  <SectionHeader icon="fa-chart-bar" title="Season Stats" />
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {sportFields.map((f) => (
                      <div
                        className="bg-white/5 rounded-xl p-5 border border-white/[0.08] text-center hover:border-white/20 transition-colors"
                        key={f.key}
                      >
                        <p className="text-3xl font-bold text-white mb-1">
                          {effectiveStats[f.key] ?? "-"}
                        </p>
                        <p className="text-[10px] font-bold text-white/30 uppercase tracking-wider">
                          {f.label}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Recent Games */}
              <section>
                <SectionHeader icon="fa-calendar-check" title="Recent Games" />
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-white/30 text-[10px] uppercase tracking-wider py-3 px-4 font-bold">
                          Date
                        </th>
                        <th className="text-left text-white/30 text-[10px] uppercase tracking-wider py-3 px-4 font-bold">
                          Opponent
                        </th>
                        <th className="text-center text-white/30 text-[10px] uppercase tracking-wider py-3 px-4 font-bold">
                          Result
                        </th>
                        <th className="text-center text-white/30 text-[10px] uppercase tracking-wider py-3 px-4 font-bold">
                          Rating
                        </th>
                        <th className="text-left text-white/30 text-[10px] uppercase tracking-wider py-3 px-4 font-bold">
                          Key Stats
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentGames.map((game, i) => (
                        <tr
                          className="border-b border-white/[0.05] hover:bg-white/[0.03] transition-colors"
                          key={i}
                        >
                          <td className="py-3 px-4 text-white/50">
                            {game.date}
                          </td>
                          <td className="py-3 px-4 text-white font-medium">
                            {game.opponent}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span
                              className={`px-2.5 py-1 rounded-md text-xs font-bold ${
                                game.result.startsWith("W")
                                  ? "bg-green-500/15 text-green-400"
                                  : game.result.startsWith("L")
                                    ? "bg-red-500/15 text-red-400"
                                    : "bg-amber-500/15 text-amber-400"
                              }`}
                            >
                              {game.result}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span
                              className={`font-bold ${
                                game.rating >= 8
                                  ? "text-green-400"
                                  : game.rating >= 7
                                    ? "text-amber-400"
                                    : "text-red-400"
                              }`}
                            >
                              {game.rating}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-white/60">
                            {game.keyStats}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {/* ═══════════════ CAREER TAB ═══════════════ */}
          {activeTab === "career" && (
            <>
              {/* Contract Info */}
              <section>
                <SectionHeader
                  icon="fa-file-contract"
                  title="Contract & Info"
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {player.dateSigned && (
                    <InfoCard
                      label="Date Signed"
                      value={new Date(player.dateSigned).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )}
                    />
                  )}
                  {player.strSigning && (
                    <InfoCard label="Signing" value={player.strSigning} />
                  )}
                  {player.strAgent && (
                    <InfoCard label="Agent" value={player.strAgent} />
                  )}
                  {player.strKit && (
                    <InfoCard label="Kit / Equipment" value={player.strKit} />
                  )}
                  {player.strWage && (
                    <InfoCard label="Wage" value={player.strWage} />
                  )}
                  {player.strOutfitter && (
                    <InfoCard label="Outfitter" value={player.strOutfitter} />
                  )}
                </div>
              </section>

              {/* Career History */}
              <section>
                <SectionHeader icon="fa-briefcase" title="Career History" />
                <div className="space-y-3">
                  {careerHistory.map((entry, i) => (
                    <div
                      className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/[0.08] hover:border-white/20 transition-colors"
                      key={i}
                    >
                      <div className="w-12 h-12 rounded-full bg-blue-600/20 flex items-center justify-center shrink-0">
                        <span className="text-lg font-bold text-blue-400">
                          {entry.club.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">
                          {entry.club}
                        </p>
                        <p className="text-white/40 text-xs">{entry.years}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-white/60 text-sm font-medium">
                          {entry.apps} apps
                        </p>
                        {entry.goals !== null && (
                          <p className="text-white/30 text-xs">
                            {entry.goals} goals
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Milestones */}
              <section>
                <SectionHeader
                  icon="fa-flag-checkered"
                  title="Career Milestones"
                />
                <div className="relative">
                  <div className="absolute left-[23px] top-2 bottom-2 w-px bg-white/10" />
                  <div className="space-y-4">
                    {milestones.map((m, i) => (
                      <div className="flex gap-4 relative" key={i}>
                        <div className="w-12 h-12 rounded-full bg-blue-600/20 border-2 border-blue-500/30 flex items-center justify-center shrink-0 z-10">
                          <i
                            className={`fas ${m.icon} text-blue-400 text-sm`}
                          />
                        </div>
                        <div className="flex-1 bg-white/5 rounded-xl p-4 border border-white/[0.08] hover:border-white/20 transition-colors">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-white font-semibold text-sm">
                              {m.title}
                            </p>
                            <span className="text-white/30 text-xs">
                              {new Date(m.date).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                              })}
                            </span>
                          </div>
                          <p className="text-white/50 text-xs">
                            {m.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </>
          )}

          {/* ═══════════════ HONOURS TAB ═══════════════ */}
          {activeTab === "honours" && (
            <>
              {/* Trophies */}
              <section>
                <SectionHeader icon="fa-trophy" title="Trophy Cabinet" />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {trophies.map((trophy, i) => (
                    <div
                      className="bg-gradient-to-b from-amber-500/10 to-transparent rounded-xl p-5 border border-amber-500/20 text-center hover:border-amber-500/40 transition-colors group"
                      key={i}
                    >
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-500/15 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <i
                          className={`fas ${trophy.icon} text-amber-400 text-lg`}
                        />
                      </div>
                      <p className="text-white font-semibold text-sm mb-1 leading-tight">
                        {trophy.name}
                      </p>
                      <p className="text-amber-400 font-bold text-2xl mb-0.5">
                        {trophy.count}x
                      </p>
                      <p className="text-white/30 text-[10px] uppercase tracking-wider">
                        Last: {trophy.year}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Awards */}
              <section>
                <SectionHeader icon="fa-award" title="Awards & Honours" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {awards.map((award, i) => (
                    <div
                      className="bg-white/5 rounded-xl p-5 border border-white/[0.08] hover:border-purple-500/30 transition-colors group"
                      key={i}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-purple-500/15 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <i
                            className={`fas ${award.icon} text-purple-400 text-sm`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm truncate">
                            {award.name}
                          </p>
                          <p className="text-white/30 text-[10px] uppercase tracking-wider">
                            {award.years}
                          </p>
                        </div>
                      </div>
                      <p className="text-purple-400 font-bold text-3xl">
                        {award.count}x
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

          {/* ═══════════════ MEDIA TAB ═══════════════ */}
          {activeTab === "media" && (
            <>
              {/* Media Gallery */}
              {galleryImages.length > 0 && (
                <section>
                  <SectionHeader icon="fa-images" title="Media Gallery" />
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                    {galleryImages.map((img, i) => (
                      <button
                        className="shrink-0 relative rounded-xl overflow-hidden border border-white/[0.08] hover:border-blue-500/40 transition-all group"
                        key={i}
                        onClick={() => setGalleryOpen(i)}
                      >
                        <img
                          alt={img.label}
                          className="h-48 w-auto object-cover group-hover:scale-105 transition-transform duration-300"
                          src={img.src}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                          <p className="text-white/70 text-[10px] font-medium uppercase tracking-wider">
                            {img.label}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Lightbox */}
                  {galleryOpen !== null && (
                    <div
                      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center backdrop-blur-sm"
                      role="presentation"
                      onClick={(event) => {
                        if (event.target === event.currentTarget) {
                          setGalleryOpen(null);
                        }
                      }}
                    >
                      <button
                        className="absolute top-6 right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                        onClick={() => setGalleryOpen(null)}
                      >
                        <i className="fas fa-times text-white" />
                      </button>
                      {galleryOpen > 0 && (
                        <button
                          className="absolute left-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setGalleryOpen(galleryOpen - 1);
                          }}
                        >
                          <i className="fas fa-chevron-left text-white" />
                        </button>
                      )}
                      {galleryOpen < galleryImages.length - 1 && (
                        <button
                          className="absolute right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setGalleryOpen(galleryOpen + 1);
                          }}
                        >
                          <i className="fas fa-chevron-right text-white" />
                        </button>
                      )}
                      <img
                        alt={galleryImages[galleryOpen].label}
                        className="max-h-[85vh] max-w-[90vw] object-contain"
                        src={galleryImages[galleryOpen].src}
                      />
                      <p className="absolute bottom-6 text-white/50 text-sm">
                        {galleryImages[galleryOpen].label} ({galleryOpen + 1}/
                        {galleryImages.length})
                      </p>
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebPlayerPlayerDetail;
